import { type IllustrationType, Text } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Stack } from "@mui/material";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib";
import { logEvent } from "@src/appV2/lib/analytics";
import { ShiftWindow } from "@src/appV2/Shifts/Shift/types";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";
import { differenceInHours, parseISO } from "date-fns";
import pluralize from "pluralize";
import { useMemo } from "react";

import { ShiftBottomSheetBookabilityCriteriaCard } from "./BookabilityCriteriaCard";
import { type ShiftBottomSheetInnerCardIntent } from "./InnerCard";
import { useReliableWindowingSequence } from "./useReliableWindowingSequence";

interface WindowContent {
  title: string | React.ReactNode;
  description: React.ReactNode;
}

interface CardConfig {
  illustration: IllustrationType;
  intent?: ShiftBottomSheetInnerCardIntent;
}

interface WindowedOutCardProps {
  windowName: string;
  windowScoreThreshold?: number;
  score?: number;
  shiftStartIso?: string;
  shiftId: string;
  workplaceId: string;
}

export function WindowedOutCard(props: WindowedOutCardProps) {
  const { minVerifiedShiftsForFamiliarity, daysToEvaluateFamiliarity } =
    useReliableWindowingSequence(props.workplaceId);

  const worker = useDefinedWorker();

  // if the user score is higher, than the threshold do not show error message for CBH score strats, suggesting that the CBH score is too low
  const showGenericMessage =
    isDefined(props.score) &&
    isDefined(props.windowScoreThreshold) &&
    props.score >= props.windowScoreThreshold;

  const showWindowThreshold =
    isDefined(props.score) &&
    isDefined(props.windowScoreThreshold) &&
    props.score < props.windowScoreThreshold;

  const estimation = useMemo(() => {
    const leadTimeInHours = props.shiftStartIso
      ? differenceInHours(parseISO(props.shiftStartIso), new Date())
      : undefined;

    if (!leadTimeInHours) {
      return "in a few minutes!";
    }

    if (leadTimeInHours > 24 * 7) {
      return "later today!";
    }

    if (leadTimeInHours > 24) {
      return "in a couple hours!";
    }

    return "in a few minutes!";
  }, [props.shiftStartIso]);

  const windowContent = useMemo((): WindowContent => {
    const isClipboardScoreWindow =
      props.windowName === ShiftWindow.CLIPBOARD_SCORE_FIRST ||
      props.windowName === ShiftWindow.CLIPBOARD_SCORE_SECOND;

    if (isClipboardScoreWindow && !showWindowThreshold) {
      logEvent(APP_V2_USER_EVENTS.SHIFT_DETAILS_SCORING_WINDOW_DONT_SHOW_THRESHOLD, {
        workerId: worker._id,
        msa: worker.address?.metropolitanStatisticalArea,
        shiftId: props.shiftId,
        windowName: props.windowName,
        showWindowThreshold,
        userScore: props.score,
        windowScoreThreshold: props.windowScoreThreshold,
      });
    }

    if (props.windowName === ShiftWindow.RELIABLE_AND_FAMILIAR) {
      const isScoreAboveThreshold =
        isDefined(props.score) &&
        isDefined(props.windowScoreThreshold) &&
        props.score >= props.windowScoreThreshold;

      const description = isScoreAboveThreshold
        ? `This shift is being offered first to professionals who have worked at least ${minVerifiedShiftsForFamiliarity} ${pluralize(
            "shift",
            minVerifiedShiftsForFamiliarity
          )} at this workplace in the last ${daysToEvaluateFamiliarity} days at this workplace.`
        : `This shift is being offered first to professionals who have recently worked here and have ${
            showWindowThreshold
              ? `at least ${props.windowScoreThreshold} pts`
              : "a high Clipboard Score"
          }.`;

      return {
        title: `You can't book yet. Check back ${estimation}`,
        description: (
          <Stack spacing={4}>
            <Box>{description}</Box>
            {isDefined(props.score) && !isScoreAboveThreshold && (
              <Text color={(theme) => theme.palette.text.secondary} variant="body2">
                You currently have {props.score} pts.
              </Text>
            )}
          </Stack>
        ),
      };
    }

    if (isClipboardScoreWindow && !showGenericMessage) {
      return {
        title: `You can't book yet. Check back ${estimation}`,
        description: (
          <Stack spacing={4}>
            <Box>
              This shift is being offered first to professionals with{" "}
              {showWindowThreshold
                ? `at least ${props.windowScoreThreshold} pts`
                : "a high Clipboard Score"}
              .
            </Box>
            {isDefined(props.score) && (
              <Text color={(theme) => theme.palette.text.secondary} variant="body2">
                You currently have {props.score} pts.
              </Text>
            )}
          </Stack>
        ),
      };
    }

    if (props.windowName === ShiftWindow.FAMILIAR_WORKERS) {
      return {
        title: `You can't book yet. Check back ${estimation}`,
        description: (
          <Stack spacing={4}>
            <Box>
              This shift is being offered first to those who have recently worked here and have{" "}
              {showWindowThreshold
                ? `at least ${props.windowScoreThreshold} pts`
                : "a high Clipboard Score"}
              .
            </Box>
            {isDefined(props.score) && (
              <Text color={(theme) => theme.palette.text.secondary} variant="body2">
                You currently have {props.score} pts.
              </Text>
            )}
          </Stack>
        ),
      };
    }

    return {
      title: "Unavailable",
      description: "This shift is not available for you at this time.",
    };
  }, [
    props.windowName,
    props.score,
    props.shiftId,
    props.windowScoreThreshold,
    showGenericMessage,
    estimation,
    minVerifiedShiftsForFamiliarity,
    daysToEvaluateFamiliarity,
    showWindowThreshold,
    worker._id,
    worker.address?.metropolitanStatisticalArea,
  ]);

  const cardConfig = useMemo((): CardConfig => {
    return {
      illustration: "hour-glass",
    };
  }, []);

  const { title, description } = windowContent;
  const { illustration, intent } = cardConfig;

  return (
    <ShiftBottomSheetBookabilityCriteriaCard
      title={title}
      description={description}
      illustrationType={illustration}
      intent={intent}
    />
  );
}
